import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TokenStorageService } from './token-storage.service';

var OAUTH_STATE_KEY = 'oauth_state';
var OAUTH_VERIFIER_KEY = 'oauth_code_verifier';
var OAUTH_RETURN_URL_KEY = 'oauth_return_url';

export interface LegacyOAuthBridge {
  token?: string;
  sessionId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OAuthClientService {
  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService
  ) {}

  startLogin(returnUrl?: string, legacyBridge?: LegacyOAuthBridge): void {
    var targetUrl = returnUrl || window.location.href;
    this.createAuthorizeUrl(targetUrl, legacyBridge).then(function(url) {
      window.location.href = url;
    });
  }

  exchangeCode(code: string, state: string): Observable<any> {
    var expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    var verifier = sessionStorage.getItem(OAUTH_VERIFIER_KEY);

    if (!state || !expectedState || state !== expectedState || !verifier) {
      throw new Error('Invalid OAuth callback state');
    }

    var body = {
      grant_type: 'authorization_code',
      client_id: environment.oauthClientId,
      code: code,
      redirect_uri: environment.oauthRedirectUri,
      code_verifier: verifier
    };

    return this.http.post<any>(environment.ssoIssuer + '/oauth/token', body).pipe(
      tap((response) => this.tokenStorage.persistOAuthSession(response)),
      switchMap((response) => {
        var token = response && response.access_token;
        if (!token) {
          return of(response);
        }

        var headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
        return this.http.get<any>(environment.ssoIssuer + '/oauth/userinfo', { headers: headers }).pipe(
          tap((profile) => this.tokenStorage.persistOAuthUserInfo(profile)),
          switchMap(() => of(response)),
          catchError(() => of(response))
        );
      }),
      tap(() => {
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
      })
    );
  }

  refreshAccessToken(): Observable<any> {
    var body = {
      grant_type: 'refresh_token',
      client_id: environment.oauthClientId,
      refresh_token: this.tokenStorage.getRefreshToken()
    };

    return this.http.post<any>(environment.ssoIssuer + '/oauth/refresh', body).pipe(
      tap((response) => this.tokenStorage.persistOAuthSession(response))
    );
  }

  getUserInfo(): Observable<any> {
    var token = this.tokenStorage.getAccessToken();
    var options = token
      ? { headers: new HttpHeaders({ Authorization: 'Bearer ' + token }) }
      : {};

    return this.http.get<any>(environment.ssoIssuer + '/oauth/userinfo', options).pipe(
      tap((profile) => this.tokenStorage.persistOAuthUserInfo(profile))
    );
  }

  logout(): Observable<any> {
    var token = this.tokenStorage.getAccessToken();
    var options = token
      ? { headers: new HttpHeaders({ Authorization: 'Bearer ' + token }) }
      : {};

    return this.http.post(environment.ssoIssuer + '/oauth/logout-all', {}, options);
  }

  consumeReturnUrl(): string {
    var returnUrl = sessionStorage.getItem(OAUTH_RETURN_URL_KEY) || '/km';
    sessionStorage.removeItem(OAUTH_RETURN_URL_KEY);
    return returnUrl;
  }

  private createAuthorizeUrl(returnUrl: string, legacyBridge?: LegacyOAuthBridge): Promise<string> {
    var state = this.randomString(32);
    var verifier = this.randomString(64);

    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier);
    sessionStorage.setItem(OAUTH_RETURN_URL_KEY, returnUrl);

    return this.sha256Base64Url(verifier).then(function(challenge) {
      var params = new URLSearchParams();
      params.set('client_id', environment.oauthClientId);
      params.set('redirect_uri', environment.oauthRedirectUri);
      params.set('response_type', 'code');
      params.set('scope', 'openid profile email');
      params.set('code_challenge', challenge);
      params.set('code_challenge_method', 'S256');
      params.set('state', state);

      if (legacyBridge && legacyBridge.token) {
        params.set('token', legacyBridge.token);
      }

      if (legacyBridge && legacyBridge.sessionId) {
        params.set('sessionId', legacyBridge.sessionId);
      }

      return environment.ssoIssuer + '/oauth/authorize?' + params.toString();
    });
  }

  private randomString(length: number): string {
    var bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    return this.base64Url(bytes);
  }

  private sha256Base64Url(value: string): Promise<string> {
    var data = new TextEncoder().encode(value);
    return Promise.resolve(window.crypto.subtle.digest('SHA-256', data))
      .then((digest) => this.base64Url(new Uint8Array(digest)));
  }

  private base64Url(bytes: Uint8Array): string {
    var binary = '';
    for (var index = 0; index < bytes.length; index++) {
      binary += String.fromCharCode(bytes[index]);
    }

    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}
